export type HubPageWidthTier = "narrow" | "default" | "wide";

export type HubViewportChrome =
  | "standard"
  | "compactHeader"
  | "filtersRail"
  | "extendedHeader"
  | "extraToolbar";

export type HubWorkspacePanelScroll = "auto" | "hidden" | "visible";

export type HubModalShellSize = "form" | "document" | "preview" | "full";

export type HubPreviewFrameMode = "inline" | "fill";

const VIEWPORT_CHROME_OFFSET_REM: Record<HubViewportChrome, string> = {
  standard: "14rem",
  compactHeader: "10rem",
  filtersRail: "10rem",
  extendedHeader: "16rem",
  extraToolbar: "18rem",
};

export function hubWorkspacePageClassName(): string {
  return "flex min-h-0 flex-1 flex-col gap-4 overflow-hidden";
}

export function hubWorkspaceHeaderClassName(): string {
  return "shrink-0";
}

export function hubWorkspaceBodyClassName(): string {
  return "flex min-h-0 flex-1 flex-col overflow-y-auto lg:overflow-hidden";
}

export function hubWorkspacePanelClassName(scroll: HubWorkspacePanelScroll = "auto"): string {
  const base = "min-h-0 min-w-0 flex flex-col";
  if (scroll === "hidden") {
    return `${base} overflow-hidden`;
  }
  if (scroll === "visible") {
    return `${base} overflow-visible`;
  }
  return `${base} overflow-y-auto pb-[7px]`;
}

export function hubFillPreviewFrameClassName(): string {
  return "h-full min-h-0 w-full flex-1 rounded-md border border-slate-200 bg-white";
}

export function hubInlinePreviewFrameClassName(): string {
  return "max-h-[min(80vh,900px)] min-h-[280px] w-full rounded-md border border-slate-200 bg-white";
}

export function hubPreviewFrameClassName(mode: HubPreviewFrameMode): string {
  return mode === "fill" ? hubFillPreviewFrameClassName() : hubInlinePreviewFrameClassName();
}

export function hubViewportTableCardClassName(chrome: HubViewportChrome = "standard"): string {
  const offset = VIEWPORT_CHROME_OFFSET_REM[chrome];
  return `flex min-h-0 flex-col overflow-hidden lg:max-h-[calc(100vh-${offset})]`;
}

export function hubViewportInboxShellClassName(): string {
  return "flex min-h-0 flex-col gap-4 lg:h-[calc(100vh-10rem)]";
}

export function hubTableScrollRegionClassName(): string {
  return "min-h-0 flex-1 overflow-auto";
}

export function hubStickyTableHeadClassName(variant: "slate" | "white" = "slate"): string {
  return variant === "white"
    ? "sticky top-0 z-20 border-b border-slate-200 bg-white shadow-[0_1px_0_0_rgb(226_232_240)]"
    : "sticky top-0 z-20 border-b border-slate-200 bg-slate-50 shadow-[0_1px_0_0_rgb(226_232_240)]";
}

export function hubModalShellClassName(size: HubModalShellSize): string {
  switch (size) {
    case "form":
      return "my-4 flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl";
    case "document":
      return "my-4 flex max-h-[calc(100dvh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl";
    case "preview":
      return "my-4 flex max-h-[min(92dvh,56rem)] w-[min(96vw,90rem)] max-w-none flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl";
    case "full":
      return "my-2 flex max-h-[calc(100dvh-1rem)] w-[min(1400px,calc(100vw-1rem))] max-w-none flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl";
  }
}

export function hubModalBodyScrollClassName(): string {
  return "min-h-0 flex-1 overflow-auto";
}

/** Document detail dialog overlay. The dialog stretches to this box instead of sitting as a short band. */
export function hubDocumentPreviewModalOverlayClassName(): string {
  return "items-stretch justify-center overflow-hidden p-3 sm:p-4";
}

/** Document detail dialog shell. Fills the overlay so previews size to the viewport. */
export function hubDocumentPreviewModalShellClassName(): string {
  return "my-0 flex h-full max-h-full min-h-0 w-full max-w-[1920px] flex-col self-stretch overflow-hidden";
}

/** Document detail dialog body. One flex column under the header; columns inside own their scroll. */
export function hubDocumentPreviewModalBodyClassName(): string {
  return "flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-3";
}
